import { useCallback, useEffect, useState } from 'react';
import { deleteAudioContent, listAudioContent, renameAudioContent } from '../api/audioApi';
import { AudioContentItem, AudioFormat } from '../types';

// Dispatched after a successful generation so the Library refreshes live.
export const AUDIO_CREATED_EVENT = 'scoutcast:audio-created';

export interface LibraryState {
  items: AudioContentItem[];
  loading: boolean;
  error: string | null;
  refresh: () => void;
  rename: (id: number, title: string) => Promise<void>;
  remove: (id: number) => Promise<void>;
}

export function useLibrary(format?: AudioFormat): LibraryState {
  const [items, setItems] = useState<AudioContentItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await listAudioContent({ format });
      setItems(list);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load library');
    } finally {
      setLoading(false);
    }
  }, [format]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const onCreated = () => load();
    window.addEventListener(AUDIO_CREATED_EVENT, onCreated);
    return () => window.removeEventListener(AUDIO_CREATED_EVENT, onCreated);
  }, [load]);

  const rename = useCallback(async (id: number, title: string) => {
    await renameAudioContent(id, title);
    setItems((prev) => prev.map((it) => (it.id === id ? { ...it, title } : it)));
  }, []);

  const remove = useCallback(async (id: number) => {
    await deleteAudioContent(id);
    setItems((prev) => prev.filter((it) => it.id !== id));
  }, []);

  return { items, loading, error, refresh: load, rename, remove };
}
